<template>
    <!-- Field Display -->
    <div v-if="!field.isEditing"
         class="field-display"
         :class="{
             'clickable': !isSectionEditing && !field.toast,
             'field-success': field.toast?.type === 'success',
             'field-error': field.toast?.type === 'error'
         }"
         @click="!isSectionEditing && !field.toast && $emit('enable-field-edit', field)"
         :tabindex="(!isSectionEditing && !field.toast) ? 0 : -1"
         @keydown.enter.prevent="!isSectionEditing && !field.toast && $emit('enable-field-edit', field)">
        <label class="field-label" :class="{'required-field': field.required}" :for="'field-display-' + field.id">{{
                field.label
            }}</label>
        <div class="field-value" :id="'field-display-' + field.id">
            <template v-if="field.value">
                <template v-if="Array.isArray(field.value)">{{ field.value.join(', ') }}</template>
                <template v-else>{{ field.value }}</template>
            </template>
            <template v-else>
                <span class="placeholder-text">—</span>
            </template>
        </div>
        <div class="edit-actions" v-if="!isSectionEditing">
            <div v-if="field.toast" class="inline-toast" :class="field.toast.type">
                <Icon v-if="field.toast.type === 'success'" name="check" size="lg" color="inherit" />
                <Icon v-else name="fa-solid fa-exclamation-circle" size="lg" color="inherit" />
                <span>{{ field.toast.message }}</span>
            </div>
            <button v-else @click.stop="$emit('enable-field-edit', field)" @keydown.enter.prevent="$emit('enable-field-edit', field)" class="button-edit-inline"
                    title="Edit Field">
                <Icon name="pencil" size="lg" color="var(--primary-400)"></Icon>
            </button>
        </div>
    </div>

    <!-- Field Edit -->
    <div v-else class="field-edit field-display"
         :class="{ 'field-error': field.error }"
         @keydown.tab="$emit('handle-field-tab', $event, field)"
         @keydown.enter="$emit('handle-enter-key', $event, field)">
        <label class="field-label" :class="{'required-field': field.required}" :for="'field-input-' + field.id">{{
                field.label
            }}</label>

        <!-- Special layout for dropdowns -->
        <div v-if="field.type === 'select' || (field.options && !field.multiselect && field.type !== 'boolean')"
             class="input-with-actions">
            <div class="select-wrapper">
                <select :id="'field-input-' + field.id"
                        ref="inputRef"
                        v-model="field.value"
                        :class="['input-field', field.error ? 'input-error' : '']">
                    <option v-for="option in field.options" :key="option" :value="option">{{ option }}</option>
                </select>
                <Icon name="chevron-down" class="select-chevron" size="lg" color="var(--grayscale-700)"></Icon>
            </div>
            <div v-if="!isSectionEditing" class="edit-actions-inline-save">
                <button @click.stop="$emit('cancel-field-edit', field)" @keydown.esc="$emit('cancel-field-edit', field)"
                        @keydown.enter.prevent="$emit('cancel-field-edit', field)" class="button-cancel-inline" title="Cancel" tabindex="0">
                    <Icon name="close" color="var(--danger-500)" size="xl"/>
                </button>
                <button @click.stop="$emit('save-field-edit', field)" class="button-save-inline" title="Save" tabindex="0">
                    <Icon name="check" color="var(--success-500)" size="xl"/>
                </button>
            </div>
        </div>

        <!-- Original layout for all other fields -->
        <template v-else>
            <div class="input-with-actions">
                <div class="input-wrapper-inner">
                    <textarea v-if="field.type === 'textarea'"
                              :id="'field-input-' + field.id"
                              ref="inputRef"
                              v-model="field.value"
                              :class="['input-field', field.error ? 'input-error' : '']">
                    </textarea>
                    <div v-else-if="field.multiselect" class="checkbox-group" :id="'field-input-' + field.id">
                        <label v-for="(option, index) in field.options" :key="option" class="checkbox-label"
                               :for="'field-input-' + field.id + '-' + index">
                            <input type="checkbox"
                                   :id="'field-input-' + field.id + '-' + index"
                                   :value="option"
                                   v-model="field.value">
                            {{ option }}
                        </label>
                    </div>
                    <div v-else-if="field.type === 'boolean' || (field.options && field.options.length === 2 && field.options.includes('Yes') && field.options.includes('No'))"
                         class="radio-group" :id="'field-input-' + field.id">
                        <template v-if="field.type === 'boolean'">
                            <label class="radio-label" :for="'field-input-' + field.id + '-yes'">
                                <input type="radio" :id="'field-input-' + field.id + '-yes'" :name="field.id" :value="true"
                                       v-model="field.value"> Yes
                            </label>
                            <label class="radio-label" :for="'field-input-' + field.id + '-no'">
                                <input type="radio" :id="'field-input-' + field.id + '-no'" :name="field.id" :value="false"
                                       v-model="field.value"> No
                            </label>
                        </template>
                        <template v-else>
                            <label v-for="(option, index) in field.options" :key="option" class="radio-label"
                                   :for="'field-input-' + field.id + '-' + index">
                                <input type="radio"
                                       :id="'field-input-' + field.id + '-' + index"
                                       :name="field.id"
                                       :value="option"
                                       v-model="field.value">
                                {{ option }}
                            </label>
                        </template>
                    </div>
                    <input v-else
                           :id="'field-input-' + field.id"
                           ref="inputRef"
                           :type="field.type === 'email' ? 'email' : 'text'"
                           v-model="field.value"
                           :class="['input-field', field.error ? 'input-error' : '']">
                </div>
                <div v-if="!isSectionEditing" class="edit-actions-inline-save">
                    <button @click.stop="$emit('cancel-field-edit', field)" @keydown.esc="$emit('cancel-field-edit', field)"
                            @keydown.enter.prevent="$emit('cancel-field-edit', field)" class="button-cancel-inline" title="Cancel" tabindex="0">
                        <Icon name="close" color="var(--danger-500)" size="xl"/>
                    </button>
                    <button @click.stop="$emit('save-field-edit', field)" class="button-save-inline" title="Save" tabindex="0">
                        <Icon name="check" color="var(--success-500)" size="xl"/>
                    </button>
                </div>
            </div>
        </template>
        <p v-if="field.error" class="error-message"><i class="fas fa-exclamation-circle"></i>{{ field.error }}</p>
    </div>
</template>

<script setup>
import { ref } from 'vue';
import Icon from '@common/icon/Icon.vue';

const props = defineProps({
    field: {
        type: Object,
        required: true
    },
    isSectionEditing: {
        type: Boolean,
        default: false
    }
});

const emit = defineEmits([
    'enable-field-edit',
    'save-field-edit',
    'cancel-field-edit',
    'handle-field-tab',
    'handle-enter-key'
]);

const inputRef = ref(null);

defineExpose({
    focus() {
        inputRef.value?.focus();
    }
});
</script>

<style scoped>
.field-display {
    font-size: var(--text-size-md);
    padding: var(--space-2) var(--space-4) var(--space-2) var(--space-6);
    border-radius: var(--radius-xs);
    position: relative;
    transition: background-color 0.2s, border-color 0.2s;
    display: grid;
    grid-template-columns: minmax(150px, 300px) minmax(200px, 300px);
    width: 100%;
    max-width: 900px;
    align-items: center;
    gap: var(--space-4);
}

.field-display:focus,
.field-edit {
    background: var(--primary-50);
}

.field-display.clickable:hover {
    background: var(--grayscale-50);
    cursor: pointer;
}

.field-success {
    background-color: var(--success-50) !important;
    border: 1px solid var(--success-300);
}

.field-error {
    background-color: var(--danger-50) !important;
    border: 1px solid var(--danger-300);
}

.inline-toast {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    font-size: var(--text-size-sm);
    font-weight: 500;
}

.inline-toast.success {
    color: var(--success-700);
}

.inline-toast.error {
    color: var(--danger-700);
}

.field-label {
    font-weight: var(--text-weight-medium);
    color: var(--text-color-subtitle);
    position: relative;
    min-height: 40px;
    display: flex;
    align-items: center;
}

.field-label.required-field::before {
    content: '*';
    color: var(--danger-500);
    margin-right: var(--space-1);
}

.field-value {
    color: var(--text-color-body);
    font-weight: var(--text-weight-medium);
    min-height: 40px;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    padding-right: var(--space-8);
    word-break: break-word;
    transition: padding-right 0.2s ease;
}

.field-display.field-success .field-value,
.field-display.field-error .field-value {
    padding-right: 140px;
}

.placeholder-text {
    color: var(--grayscale-400);
}

.input-wrapper-inner {
    width: 100%;
    flex: 1;
}

.input-field {
    width: 100%;
    padding: var(--space-1) var(--space-2);
    border: 1px solid var(--primary-300);
    border-radius: var(--radius-xs);
}

.input-field:focus {
    box-shadow: 0 0 0 1px var(--primary-500);
    border-color: transparent;
    outline: none;
}

.input-field.input-error {
    border-color: var(--danger-500);
    background-color: var(--danger-50);
}

textarea.input-field {
    min-height: 80px;
}

select.input-field {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    padding-right: 2rem;
}

.select-wrapper {
    position: relative;
    width: 100%;
    flex: 1;
}

.select-chevron {
    position: absolute;
    right: 0.5rem;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
}

.error-message {
    color: var(--danger-500);
    font-size: var(--text-size-xs);
    margin-top: var(--space-1);
    grid-column: 1 / -1;
}

.input-with-actions {
    display: flex;
    align-items: center;
    gap: var(--space-2);
    width: 100%;
    min-height: 40px;
}

.edit-actions {
    position: absolute;
    right: var(--space-2);
    top: 50%;
    transform: translateY(-50%);
    display: flex;
}

.field-display:not(.field-edit) .edit-actions {
    opacity: 0;
    visibility: hidden;
    transition: opacity 0.2s ease-in-out;
}

.field-display:not(.field-edit):hover .edit-actions,
.field-display:not(.field-edit):focus-within .edit-actions {
    opacity: 1;
    visibility: visible;
}

button {
    border: none;
    background: none;
    cursor: pointer;
}

.button-edit-inline {
    padding: var(--space-1);
    border-radius: var(--radius-sm);
    border: none;
    background: unset;
}

.edit-actions-inline-save {
    display: flex;
    gap: var(--space-1);
    flex-shrink: 0;
}

.button-save-inline, .button-cancel-inline {
    padding: var(--space-1);
    width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius-sm);
}

.button-save-inline:hover {
    background: var(--success-100);
}

.button-cancel-inline:hover {
    background: var(--danger-100);
}

.radio-group {
    display: flex;
    gap: var(--space-4);
    padding-top: var(--space-1);
}

.checkbox-group {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding-top: var(--space-1);
}

.radio-label, .checkbox-label {
    display: flex;
    align-items: center;
    gap: var(--space-2);
}
</style>
